// Pure lookup helpers + raw→display adapter for the Governance intelligence drawer.
import {
  FINDING_SEVERITY_TONE,
  FINDING_SEVERITY_RANK,
  GOVERNANCE_CYCLE_STATUS_TONE,
  GOVERNANCE_ROLE_ICON_CLS,
  GOVERNANCE_STRENGTH_TONE,
  NEUTRAL_TONE,
  type FindingSeverity,
  type FindingSeverityTone,
  type GovernanceCycleStatus,
  type GovernanceRoleType,
  type GovernanceStrength,
} from "../../constants";
import type {
  GApproval,
  GControl,
  GFinding,
  GOwnership,
  GReviewCycle,
  GovernanceCriterion,
  GovernanceDisplayData,
  GovernanceFindingDatapoint,
  GovernanceFindingSource,
  GovernanceRawFinding,
  GovernanceResult,
} from "../governanceTypes";

export function severityTone(
  s: GFinding["severity"] | string | null | undefined,
): FindingSeverityTone {
  const key = String(s ?? "INFO").toUpperCase() as FindingSeverity;
  return FINDING_SEVERITY_TONE[key] ?? FINDING_SEVERITY_TONE.INFO;
}

export function severityRank(s: GFinding["severity"] | string): number {
  const key = String(s ?? "INFO").toUpperCase() as FindingSeverity;
  return FINDING_SEVERITY_RANK[key] ?? FINDING_SEVERITY_RANK.INFO;
}

export function strengthTone(
  s: GControl["strength"] | string | null | undefined,
): FindingSeverityTone {
  const key = String(s ?? "ADEQUATE").toUpperCase() as GovernanceStrength;
  return GOVERNANCE_STRENGTH_TONE[key] ?? NEUTRAL_TONE;
}

export function cycleStatusTone(
  s: GReviewCycle["status"] | string | null | undefined,
): FindingSeverityTone {
  const key = String(s ?? "MISSING").toUpperCase() as GovernanceCycleStatus;
  return GOVERNANCE_CYCLE_STATUS_TONE[key] ?? NEUTRAL_TONE;
}

export function roleIconClass(
  r: GOwnership["role_type"] | string | null | undefined,
): string {
  const key = String(r ?? "STAKEHOLDER").toUpperCase() as GovernanceRoleType;
  return GOVERNANCE_ROLE_ICON_CLS[key] ?? GOVERNANCE_ROLE_ICON_CLS.STAKEHOLDER;
}

/* Sort findings by severity (HIGH first). */
export function sortFindingsBySeverity(findings: GFinding[]): GFinding[] {
  return [...findings].sort(
    (a, b) => severityRank(a.severity) - severityRank(b.severity),
  );
}

/* Count controls the document is missing. */
export function countMissingControls(controls: GControl[]): number {
  return controls.filter((c) => !c.present).length;
}

/* ────────────────────────────────────────────────────────────────
   Raw → display adapter
   Maps the backend governance-classify payload into the shape
   the drawer sub-components already know how to render.
   ──────────────────────────────────────────────────────────────── */

const NOT_STATED = "not stated";

/* Governance-maturity criterion → GControl category. */
const CRITERION_CATEGORY: Record<string, GControl["category"]> = {
  roles_defined: "OWNERSHIP",
  responsibilities_assigned: "OWNERSHIP",
  accountability_assigned: "OWNERSHIP",
  governance_structure_defined: "OWNERSHIP",
  approval_process_defined: "APPROVAL",
  decision_rights_defined: "APPROVAL",
  review_process_defined: "REVIEW",
  review_frequency_defined: "REVIEW",
  exception_process_defined: "ENFORCEMENT",
  escalation_process_defined: "ENFORCEMENT",
  oversight_defined: "MONITORING",
  governance_authority_defined: "MONITORING",
  governance_evidence_defined: "REPORTING",
  policies_standards_identified: "REPORTING",
};

/* Datapoint → ownership role_type mapping. */
const DATAPOINT_ROLE_TYPE: Record<string, GOwnership["role_type"]> = {
  ROLE: "OWNER",
  ACCOUNTABILITY: "CUSTODIAN",
  OVERSIGHT: "APPROVER",
  RESPONSIBILITY: "STAKEHOLDER",
};

const OWNERSHIP_DATAPOINTS = new Set(Object.keys(DATAPOINT_ROLE_TYPE));
const APPROVAL_DATAPOINTS = new Set(["APPROVAL", "AUTHORITY"]);
const REVIEW_DATAPOINTS = new Set(["REVIEW"]);

function humaniseKey(key: string): string {
  return key
    .split("_")
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

function padId(index: number): string {
  return `GOV-${String(index + 1).padStart(2, "0")}`;
}

function firstNonNotStated(...values: Array<string | undefined | null>): string {
  for (const v of values) {
    const s = (v ?? "").trim();
    if (s && s.toLowerCase() !== NOT_STATED) return s;
  }
  return "";
}

function formatSourceLocation(sources: GovernanceFindingSource[] | undefined): string {
  const first = sources?.[0];
  if (!first) return "-";
  const section = first.section ? `§${first.section}` : "";
  const page = first.page ? `p.${first.page}` : "";
  const heading = first.heading?.trim() ?? "";
  return [section, page, heading].filter(Boolean).join(" · ") || "-";
}

function criterionStrength(c: GovernanceCriterion): GControl["strength"] {
  if (c.status === "PRESENT") return c.records >= 2 ? "STRONG" : "ADEQUATE";
  if (c.status === "PARTIAL") return "WEAK";
  return "MISSING";
}

function buildControlsFromCriteria(
  criteria: Record<string, GovernanceCriterion>,
): GControl[] {
  return Object.entries(criteria).map(([key, criterion], index) => {
    const evidenceText = criterion.evidence?.[0]?.source_text ?? "";
    return {
      control_id: padId(index),
      control_name: humaniseKey(key),
      category: CRITERION_CATEGORY[key] ?? "MONITORING",
      present: criterion.status === "PRESENT",
      strength: criterionStrength(criterion),
      source_location:
        evidenceText.trim() ||
        (criterion.detail?.trim() ? "-" : criterion.status),
    };
  });
}

function buildGapFindings(
  criteria: Record<string, GovernanceCriterion>,
): GFinding[] {
  return Object.entries(criteria)
    .filter(([, c]) => c.status === "ABSENT" || c.status === "PARTIAL")
    .map(([key, c]) => {
      const controlName = humaniseKey(key);
      return {
        finding_type: `${controlName} missing`,
        affected_control: controlName,
        description:
          (c.detail && c.detail.trim()) ||
          `No records satisfying "${controlName}" were extracted from this document.`,
        severity: c.status === "PARTIAL" ? "MEDIUM" : "HIGH",
        source_location: [],
      } satisfies GFinding;
    });
}

function buildOwnership(findings: GovernanceRawFinding[]): GOwnership[] {
  return findings
    .filter((f) => OWNERSHIP_DATAPOINTS.has(f.datapoint))
    .map((f) => {
      const named = firstNonNotStated(f.assigned_to, f.actor);
      const responsibilities: string[] = f.statement
        ? [f.statement].filter(Boolean)
        : [];
      return {
        role: f.name,
        role_type: DATAPOINT_ROLE_TYPE[f.datapoint] ?? "STAKEHOLDER",
        named_party: named || undefined,
        responsibilities,
        source_location: formatSourceLocation(f.sources),
        clarity_score: f.statement ? 1 : 0.5,
      } satisfies GOwnership;
    });
}

function buildApprovals(findings: GovernanceRawFinding[]): GApproval[] {
  return findings
    .filter((f) => APPROVAL_DATAPOINTS.has(f.datapoint))
    .map((f) => ({
      authority: f.name,
      approval_type:
        f.datapoint === "AUTHORITY" ? "EXCEPTION_APPROVAL" : "POLICY_APPROVAL",
      effective_date: undefined,
      documented: Boolean(f.sources?.[0]?.source_text),
      source_location: formatSourceLocation(f.sources),
    }));
}

function buildReviewCycles(findings: GovernanceRawFinding[]): GReviewCycle[] {
  return findings
    .filter((f) => REVIEW_DATAPOINTS.has(f.datapoint))
    .map((f) => ({
      activity: f.name,
      frequency: firstNonNotStated(f.frequency) || "As required",
      owner: firstNonNotStated(f.actor, f.assigned_to) || "Not stated",
      status: "DEFINED",
      source_location: formatSourceLocation(f.sources),
    }));
}

function sumRecords(
  byDatapoint: Record<string, number> | undefined,
  keys: readonly GovernanceFindingDatapoint[],
): number {
  if (!byDatapoint) return 0;
  return keys.reduce((acc, k) => acc + (byDatapoint[k] ?? 0), 0);
}

export function adaptGovernanceResult(
  raw: GovernanceResult,
  documentTitle?: string,
  documentType?: string,
): GovernanceDisplayData {
  const maturity = raw.metrics?.governance_maturity;
  const criteria = maturity?.criteria ?? {};
  const counts = maturity?.counts ?? { present: 0, partial: 0, absent: 0 };
  const rbd = raw.metrics?.records_by_datapoint;
  const rawFindings = raw.findings ?? [];

  const controls = buildControlsFromCriteria(criteria);
  const findings = buildGapFindings(criteria);
  const ownership = buildOwnership(rawFindings);
  const approvals = buildApprovals(rawFindings);
  const reviewCycles = buildReviewCycles(rawFindings);

  const controlsExpected =
    maturity?.criteria_total ?? Object.keys(criteria).length;
  const controlsPresent = counts.present ?? 0;
  const controlsMissing = counts.absent ?? 0;
  const strongControls = controls.filter((c) => c.strength === "STRONG").length;
  const weakControls =
    (counts.partial ?? 0) ||
    controls.filter((c) => c.strength === "WEAK").length;

  const namedOwners =
    sumRecords(rbd, ["RESPONSIBILITY", "ROLE", "ACCOUNTABILITY", "OVERSIGHT"]) ||
    ownership.length;
  const approversIdentified =
    sumRecords(rbd, ["APPROVAL", "AUTHORITY"]) || approvals.length;
  const reviewCyclesDefined =
    sumRecords(rbd, ["REVIEW"]) || reviewCycles.length;
  const governanceScore =
    controlsExpected > 0 ? controlsPresent / controlsExpected : 0;

  return {
    document: {
      document_id: raw.documentId ?? "",
      title: documentTitle ?? "",
      document_type: documentType,
    },
    summary: {
      controls_expected: controlsExpected,
      controls_present: controlsPresent,
      controls_missing: controlsMissing,
      strong_controls: strongControls,
      weak_controls: weakControls,
      named_owners: namedOwners,
      approvers_identified: approversIdentified,
      review_cycles_defined: reviewCyclesDefined,
      governance_score: governanceScore,
      findings: findings.length,
    },
    ownership,
    approvals,
    review_cycles: reviewCycles,
    controls,
    findings,
    backendNote: raw.summary?.trim() || undefined,
  };
}
